// ---------------------------------------------------------------------------
// cogibulle diffusion — Runtime embarqué (appli native iOS / macOS / Android)
//
// Quand la coquille appli (Meteor + Cordova, voir appli/README.md à la racine
// du monorepo) fait tourner ce client, il n'y a AUCUN serveur : les contenus
// sont dans public/ (assets/, hype/, embarque/manifest.json + app.json).
// Ce module :
//   1. détecte le mode embarqué ;
//   2. coupe DDP (aucune tentative de connexion) ;
//   3. hydrate Minimongo avec les documents miroir — même mapping que
//      l'ingestion d'un serveur de diffusion (imports/diffusion_miroir_docs.js) ;
//   4. expose l'état réactif (prêt, version, app.json) et l'API de
//      verrouillage des scènes (« Accès libre » vs achat in-app).
// ---------------------------------------------------------------------------

import { Meteor } from 'meteor/meteor';
import { ReactiveVar } from 'meteor/reactive-var';

import {
  Projets, Scenes, Cartes, HubFiles, HypeWidgets,
  DiffusionSceneAccess, DiffusionVersions,
} from '../../imports/collections.js';
import { docs_miroir_hub_depuis_payload } from '../../imports/diffusion_miroir_docs.js';

const gEmbarquePret = new ReactiveVar (false);
const gEmbarqueVersionId = new ReactiveVar (null);
const gEmbarqueApp = new ReactiveVar (null);       // contenu de app.json
const gEmbarqueAchats = new ReactiveVar ({acces_complet : false});
const gEmbarqueErreur = new ReactiveVar (null);

let gEmbarqueDocuments = {};                       // {url d'origine : [pages]}

let gDemarrageLance = false;

// Mode embarqué si : build Cordova (cas nominal de l'appli), réglage public
// `embarque` (tests navigateur du workspace appli), ou flag dev posé en
// console sur un serveur diffusion classique.
export function est_runtime_embarque ()
  {
    if (Meteor.isCordova) return true;
    if (Meteor.settings && Meteor.settings.public && Meteor.settings.public.embarque)
      return true;
    return typeof window !== 'undefined' && window.__COGIBULLE_EMBARQUE_DEV__ === true;
  }

export function runtime_embarque_pret ()
  {
    return gEmbarquePret.get ();
  }

export function version_id_embarque ()
  {
    return gEmbarqueVersionId.get ();
  }

export function app_embarque ()
  {
    return gEmbarqueApp.get ();
  }

// Pages (images) de la version embarquée d'un document en ligne, ou null.
// Table fabriquée par appli/embarque_documents.js.
export function pages_document_embarque (pUrl)
  {
    const tPages = typeof pUrl === 'string' ? gEmbarqueDocuments[pUrl.trim ()] : null;
    return Array.isArray (tPages) && tPages.length ? tPages : null;
  }

export function erreur_runtime_embarque ()
  {
    return gEmbarqueErreur.get ();
  }

// Verrouillage (modèle « achat unique » seulement) : une scène est verrouillée
// si elle n'est PAS « Accès libre » dans le hub et que l'achat unique acces_complet n'a été ni effectué ni
// restauré. Une cible inconnue (pas de doc d'accès) n'est jamais verrouillée,
// comme dans le lecteur web. L'état des achats est alimenté par le module
// client/imports/achat_embarque/ (cordova-plugin-purchase) et persiste en
// localStorage pour être disponible dès le boot, hors ligne.
// Modèle « gratuit » (rubrique Applis du hub, app.json) : aucun verrou, aucun
// achat intégré. Un app.json d'avant ce réglage vaut « achat unique ».
export function appli_embarquee_gratuite ()
  {
    const tApp = gEmbarqueApp.get ();
    return !!(tApp && tApp.monetisation && tApp.monetisation.modele === 'gratuit');
  }

export function scene_verrouillee_embarque (pSlugOuId)
  {
    const tCle = typeof pSlugOuId === 'string' ? pSlugOuId.trim () : '';
    if (!tCle) return false;
    if (appli_embarquee_gratuite ()) return false;
    const tAcces = DiffusionSceneAccess.findOne (
      {$or : [{scene_id : tCle}, {slug : tCle}]}
    );
    if (!tAcces || tAcces.acces_libre === true) return false;
    return !gEmbarqueAchats.get ().acces_complet;
  }

export function scene_deverrouillee_embarque (pSlugOuId)
  {
    return !scene_verrouillee_embarque (pSlugOuId);
  }

export function achats_embarque ()
  {
    return gEmbarqueAchats.get ();
  }

const CLE_ACHATS_EMBARQUE = 'cogibulle.achats_embarque';

function lit_achats_persistes ()
  {
    try
      {
        const tBrut = localStorage.getItem (CLE_ACHATS_EMBARQUE);
        return tBrut ? JSON.parse (tBrut) : null;
      }
    catch (e)
      {
        return null;
      }
  }

export function definit_achats_embarque (pAchats, pOptions = {})
  {
    const tAchats = Object.assign ({acces_complet : false}, pAchats || {});
    gEmbarqueAchats.set (tAchats);
    if (pOptions.persiste !== false)
      {
        try { localStorage.setItem (CLE_ACHATS_EMBARQUE, JSON.stringify (tAchats)); }
        catch (e) { /* stockage local indisponible : état mémoire seulement */ }
      }
  }

function insere_local (pCollection, pDoc)
  {
    // Insertion Minimongo directe : pas de méthode serveur (il n'y en a pas),
    // pas de compensation de latence.
    pCollection._collection.insert (pDoc);
  }

async function charge_json_embarque (pChemin)
  {
    const tReponse = await fetch (pChemin);
    if (!tReponse.ok)
      throw new Error (`${pChemin} : HTTP ${tReponse.status}`);
    return tReponse.json ();
  }

// Démarre le runtime embarqué (au premier appel seulement). Renvoie false si
// le mode embarqué n'est pas actif.
export function demarre_runtime_embarque ()
  {
    if (!est_runtime_embarque ()) return false;
    if (gDemarrageLance) return true;
    gDemarrageLance = true;

    // Hors ligne par construction : aucune tentative DDP.
    Meteor.disconnect ();

    // Achats persistés localement : disponibles dès le boot, avant même que
    // le store natif ait répondu (avion, première seconde de l'appli…).
    const tAchatsPersistes = lit_achats_persistes ();
    if (tAchatsPersistes) definit_achats_embarque (tAchatsPersistes, {persiste : false});

    (async () =>
      {
        const tPayload = await charge_json_embarque ('/embarque/manifest.json');
        const tApp = await charge_json_embarque ('/embarque/app.json');
        // Facultatif : absent si le projet n'ouvre aucun document en ligne.
        gEmbarqueDocuments = await charge_json_embarque ('/embarque/documents.json').catch (() => ({}));

        const tDocs = docs_miroir_hub_depuis_payload (tPayload, new Date ());
        if (!tDocs) throw new Error ('manifest embarqué invalide (projet non identifiable)');

        insere_local (Projets, tDocs.projet);
        for (const tDoc of tDocs.hub_files) insere_local (HubFiles, tDoc);
        for (const tDoc of tDocs.scenes) insere_local (Scenes, tDoc);
        for (const tDoc of tDocs.scene_access) insere_local (DiffusionSceneAccess, tDoc);
        for (const tDoc of tDocs.cartes) insere_local (Cartes, tDoc);
        for (const tDoc of tDocs.hype_widgets) insere_local (HypeWidgets, tDoc);

        // Document de version minimal : suffisant pour les helpers du lecteur
        // (accès public → aucune gate) et l'entrée de jeu par défaut.
        const tManifest = tPayload.manifest || {};
        const tGame = tManifest.game || {};
        insere_local (DiffusionVersions,
          {
            _id             : tPayload.version_id,
            game_id         : tPayload.game_id,
            game_slug       : tPayload.game_slug || tGame.slug || null,
            version         : tPayload.version || 'app',
            source          : tPayload.source || 'hub',
            active          : true,
            status          : 'active',
            access_mode     : 'public',
            published_at    : new Date (),
            manifest_summary: {
              titre       : tGame.titre || tPayload.game_slug || 'Projet',
              description : tGame.description || '',
              vignette_url: tGame.vignette_url || null,
              scenes      : tDocs.scenes.length,
              cartes      : tDocs.cartes.length,
              assets      : tDocs.hub_files.length,
            },
          }
        );

        gEmbarqueApp.set (tApp);
        gEmbarqueVersionId.set (tPayload.version_id);
        gEmbarquePret.set (true);
      }) ().catch ((pErr) =>
        {
          console.error ('[embarque] échec du démarrage du runtime', pErr);
          gEmbarqueErreur.set (pErr && pErr.message || 'Erreur inconnue');
        });

    return true;
  }
